/**
 * App accent color tokens.
 * Values are HSL triplets without the `hsl()` wrapper to match Tailwind CSS variables.
 */

export const DEFAULT_ACCENT_COLOR = 'blue';
export const CUSTOM_ACCENT_COLOR = 'custom';
export const DEFAULT_CUSTOM_ACCENT_HEX = '#2563eb';

export interface AccentColorTokens {
  primary: string;
  primaryForeground: string;
  ring: string;
}

interface AccentColorDefinition {
  id: string;
  label: string;
  swatch: string;
  light: AccentColorTokens;
  dark: AccentColorTokens;
}

export const ACCENT_COLOR_OPTIONS = [
  {
    id: 'blue',
    label: 'Default blue',
    swatch: 'hsl(221.2 83.2% 53.3%)',
    light: { primary: '221.2 83.2% 53.3%', primaryForeground: '210 40% 98%', ring: '221.2 83.2% 53.3%' },
    dark: { primary: '217.2 91.2% 59.8%', primaryForeground: '222.2 47.4% 11.2%', ring: '224.3 76.3% 48%' },
  },
  {
    id: 'violet',
    label: 'Violet',
    swatch: 'hsl(262.1 83.3% 57.8%)',
    light: { primary: '262.1 83.3% 57.8%', primaryForeground: '210 40% 98%', ring: '262.1 83.3% 57.8%' },
    dark: { primary: '263.4 70% 50.4%', primaryForeground: '210 40% 98%', ring: '263.4 70% 50.4%' },
  },
  {
    id: 'emerald',
    label: 'Emerald',
    swatch: 'hsl(160.1 84.1% 39.4%)',
    light: { primary: '160.1 84.1% 39.4%', primaryForeground: '210 40% 98%', ring: '160.1 84.1% 39.4%' },
    dark: { primary: '158.1 64.4% 51.6%', primaryForeground: '222.2 47.4% 11.2%', ring: '158.1 64.4% 51.6%' },
  },
  {
    id: 'rose',
    label: 'Rose',
    swatch: 'hsl(346.8 77.2% 49.8%)',
    light: { primary: '346.8 77.2% 49.8%', primaryForeground: '210 40% 98%', ring: '346.8 77.2% 49.8%' },
    dark: { primary: '346.8 77.2% 49.8%', primaryForeground: '210 40% 98%', ring: '346.8 77.2% 49.8%' },
  },
  {
    id: 'amber',
    label: 'Amber',
    swatch: 'hsl(37.7 92.1% 50.2%)',
    light: { primary: '37.7 92.1% 50.2%', primaryForeground: '222.2 47.4% 11.2%', ring: '37.7 92.1% 50.2%' },
    dark: { primary: '45.4 93.4% 47.5%', primaryForeground: '222.2 47.4% 11.2%', ring: '45.4 93.4% 47.5%' },
  },
  {
    id: 'slate',
    label: 'Slate',
    swatch: 'hsl(215.4 16.3% 46.9%)',
    light: { primary: '215.4 16.3% 46.9%', primaryForeground: '210 40% 98%', ring: '215.4 16.3% 46.9%' },
    dark: { primary: '215 20.2% 65.1%', primaryForeground: '222.2 47.4% 11.2%', ring: '215 20.2% 65.1%' },
  },
] as const satisfies readonly AccentColorDefinition[];

export type PresetAccentColor = (typeof ACCENT_COLOR_OPTIONS)[number]['id'];
export type AccentColor = PresetAccentColor | typeof CUSTOM_ACCENT_COLOR;
export type AccentColorOption = (typeof ACCENT_COLOR_OPTIONS)[number];

export function isPresetAccentColor(value: string | null): value is PresetAccentColor {
  return ACCENT_COLOR_OPTIONS.some((option) => option.id === value);
}

export function isAccentColor(value: string | null): value is AccentColor {
  return value === CUSTOM_ACCENT_COLOR || isPresetAccentColor(value);
}

export function getPresetAccentTokens(accentColor: PresetAccentColor, mode: 'light' | 'dark'): AccentColorTokens {
  const option = ACCENT_COLOR_OPTIONS.find((item) => item.id === accentColor) ?? ACCENT_COLOR_OPTIONS[0];
  return option[mode];
}
